import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { View } from "react-native";
import { Notification } from "./Notification.component";
import { NotificationContextProps, NotificationItem, NotificationType } from "./Notification.types";
import styles from "./Notification.styles";

const NotificationContext = createContext<NotificationContextProps>({
  show: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  hide: () => {},
  hideAll: () => {},
  notifications: [],
});

export const useNotification = () => useContext(NotificationContext);

interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
  position?: "top" | "bottom" | "center";
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ 
  children, 
  maxNotifications = 5,
  position = "bottom"
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const show = useCallback((message: string, type: NotificationType = "info", duration = 3000) => {
    const id = generateId();
    const newNotification: NotificationItem = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now(),
    };

    setNotifications(prev => {
      const updated = [...prev, newNotification];
      // Limiter le nombre de notifications affichées
      if (updated.length > maxNotifications) {
        return updated.slice(-maxNotifications);
      }
      return updated;
    });

    return id;
  }, [maxNotifications]);

  const showSuccess = useCallback((message: string, duration = 3000) => {
    return show(message, "success", duration);
  }, [show]);

  const showError = useCallback((message: string, duration = 5000) => {
    return show(message, "error", duration);
  }, [show]);

  const showWarning = useCallback((message: string, duration = 4000) => {
    return show(message, "warning", duration);
  }, [show]);

  const showInfo = useCallback((message: string, duration = 3000) => {
    return show(message, "info", duration);
  }, [show]);

  const hide = useCallback((id?: string) => {
    if (id) {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    } else {
      // Supprimer la plus ancienne si pas d'ID spécifié
      setNotifications(prev => prev.slice(1));
    }
  }, []);

  const hideAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleNotificationHide = useCallback((id: string) => {
    hide(id);
  }, [hide]);

  const contextValue: NotificationContextProps = {
    show,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hide,
    hideAll,
    notifications,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      
      {/* Conteneur des notifications - Version web améliorée */}
      <View style={styles.notificationsContainer} pointerEvents="box-none">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            position={position}
            showCloseButton={true}
            onHide={() => handleNotificationHide(notification.id)}
          />
        ))}
      </View>
    </NotificationContext.Provider>
  );
};